import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardPage } from '../../components/PremiumDesign';
import { TagsTab } from '../components/TagsTab';
import { CustomFieldsTab } from '../components/CustomFieldsTab';
import { SmartAssignmentTab } from '../components/SmartAssignmentTab';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import { AccessDenied } from '@/common/components';

const SETTINGS_TABS = [
  {
    id: 'tags',
    label: 'Tags',
    viewPerms: [PERMISSIONS.TAGS.VIEW, PERMISSIONS.CRM_CONFIG.VIEW],
    createPerms: [
      PERMISSIONS.TAGS.CREATE,
      PERMISSIONS.CRM_CONFIG.CREATE,
      PERMISSIONS.CRM_CONFIG.MANAGE,
    ],
    updatePerms: [
      PERMISSIONS.TAGS.UPDATE,
      PERMISSIONS.CRM_CONFIG.UPDATE,
      PERMISSIONS.CRM_CONFIG.MANAGE,
    ],
    deletePerms: [
      PERMISSIONS.TAGS.DELETE,
      PERMISSIONS.CRM_CONFIG.DELETE,
      PERMISSIONS.CRM_CONFIG.MANAGE,
    ],
  },
  {
    id: 'custom_fields',
    label: 'Custom Fields',
    viewPerms: [PERMISSIONS.CUSTOM_FIELDS.VIEW, PERMISSIONS.CRM_CONFIG.VIEW],
    createPerms: [
      PERMISSIONS.CUSTOM_FIELDS.CREATE,
      PERMISSIONS.CRM_CONFIG.CREATE,
      PERMISSIONS.CRM_CONFIG.MANAGE,
    ],
    updatePerms: [
      PERMISSIONS.CUSTOM_FIELDS.UPDATE,
      PERMISSIONS.CRM_CONFIG.UPDATE,
      PERMISSIONS.CRM_CONFIG.MANAGE,
    ],
    deletePerms: [
      PERMISSIONS.CUSTOM_FIELDS.DELETE,
      PERMISSIONS.CRM_CONFIG.DELETE,
      PERMISSIONS.CRM_CONFIG.MANAGE,
    ],
  },
  {
    id: 'smart_assignment',
    label: 'Auto Assignment',
    // Owner-only — never shown to members/employees.
    ownerOnly: true,
    viewPerms: [],
    createPerms: [],
    updatePerms: [],
    deletePerms: [],
  },
];

export function CRMSettingsPage() {
  const { canAny, role, permissionLoading } = usePermissions();
  const isOwner = String(role || '').toLowerCase() === 'owner';
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleTabs = useMemo(
    () =>
      SETTINGS_TABS.filter((tab) => {
        if (tab.ownerOnly) return isOwner;
        return isOwner || canAny(tab.viewPerms);
      }).map(({ id, label }) => ({ id, label })),
    [canAny, isOwner],
  );

  const requestedTab = searchParams.get('tab');
  const fallbackTab = visibleTabs[0]?.id || 'tags';
  const activeTab = visibleTabs.some((tab) => tab.id === requestedTab)
    ? requestedTab
    : fallbackTab;

  useEffect(() => {
    if (permissionLoading || visibleTabs.length === 0) return;
    if (requestedTab === activeTab) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', activeTab);
    setSearchParams(next, { replace: true });
  }, [
    permissionLoading,
    visibleTabs,
    requestedTab,
    activeTab,
    searchParams,
    setSearchParams,
  ]);

  const setActiveTab = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  const getTabAccess = (tabId) => {
    const tab = SETTINGS_TABS.find((t) => t.id === tabId);
    if (!tab) {
      return {
        canView: false,
        canCreate: false,
        canUpdate: false,
        canDelete: false,
        isReadOnly: true,
      };
    }

    if (tab.ownerOnly) {
      return {
        canView: isOwner,
        canCreate: isOwner,
        canUpdate: isOwner,
        canDelete: isOwner,
        isReadOnly: !isOwner,
      };
    }

    if (isOwner) {
      return {
        canView: true,
        canCreate: true,
        canUpdate: true,
        canDelete: true,
        isReadOnly: false,
      };
    }

    const canCreate = canAny(tab.createPerms);
    const canUpdate = canAny(tab.updatePerms);
    const canDelete = canAny(tab.deletePerms);
    return {
      canView: canAny(tab.viewPerms),
      canCreate,
      canUpdate,
      canDelete,
      isReadOnly: !canCreate && !canUpdate,
    };
  };

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object', e);
    }
    return localStorage.getItem('company_id');
  };
  const companyId = getCompanyId();

  if (visibleTabs.length === 0) {
    return <AccessDenied />;
  }

  const tagsAccess = getTabAccess('tags');
  const fieldsAccess = getTabAccess('custom_fields');
  const assignAccess = getTabAccess('smart_assignment');

  return (
    <DashboardPage className="crm-config !overflow-hidden !h-full flex-1 min-h-0 flex flex-col">
      <div className="flex flex-col flex-1 min-h-0 bg-transparent">
        <div className="flex flex-col flex-1 min-h-0 relative">
          {activeTab === 'tags' && tagsAccess.canView && (
            <TagsTab
              companyId={companyId}
              isReadOnly={tagsAccess.isReadOnly}
              canCreate={tagsAccess.canCreate}
              canUpdate={tagsAccess.canUpdate}
              canDelete={tagsAccess.canDelete}
              tabs={visibleTabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          )}
          {activeTab === 'custom_fields' && fieldsAccess.canView && (
            <CustomFieldsTab
              companyId={companyId}
              isReadOnly={fieldsAccess.isReadOnly}
              canCreate={fieldsAccess.canCreate}
              canUpdate={fieldsAccess.canUpdate}
              canDelete={fieldsAccess.canDelete}
              tabs={visibleTabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          )}
          {activeTab === 'smart_assignment' && assignAccess.canView && (
            <SmartAssignmentTab
              companyId={companyId}
              isReadOnly={assignAccess.isReadOnly}
              tabs={visibleTabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          )}
        </div>
      </div>
    </DashboardPage>
  );
}
